import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from '@mui/material'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import {
  rmnBrandsEndpoint,
  rmnCategoriesEndpoint,
  rmnDiscoveredBrandsEndpoint,
  rmnDiscoveredProductsAutoMatchEndpoint,
  rmnDiscoveredProductsEndpoint,
  rmnIgnoreDiscoveredProductEndpoint,
} from '../../api/endpoints'
import { MainContainer } from '../../components/ContentWrapper'
import { ListPageHeader } from '../../components/ListPageHeader/ListPageHeader'
import { CatalogTabs, PRODUCT_CATALOG_TABS } from './CatalogTabs'
import { ConfirmationDialog } from '../../components/ConfirmationDialog/ConfirmationDialog'
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable'
import { useCommonParams } from '../../hooks/useCommonParams'
import { useResourceList } from '../../hooks/useResourceList'
import { useNetworkScope } from '../../hooks/useNetworkScope'
import {
  RmnCategoryNode,
  RmnDiscoveredBrandOption,
  RmnDiscoveredProduct,
  RmnNetworkBrand,
} from '../../types/rmn'
import { ImportDiscoveredModal } from './ImportDiscoveredModal'
import { STATUS_FILTERS, StatusPill, isActionable } from './discoveredImportShared'
import {
  FlatCategoryOption,
  flattenRmn,
} from '../admin/productsV2/productListView/ProductFilters'

// Catalog › Products › Discovered (TDC-1499 phase 2): review the products
// the analysis found and add them to the catalog. Import opens the modal
// (required TDC category, optional link-to-existing); server-side the product
// always lands categorized and associated to the account brand (+ supplier
// for RMN networks). Reports and scores live on the Catalog tab — these rows
// aren't in the catalog yet, so there's nothing here to score.

const PAGE_SIZE = 25

export default function NetworkProductsImportPage() {
  const navigate = useNavigate()
  const { basePath, brandName, domain } = useNetworkScope()
  const analysisLabel = domain ? `${domain} analysis` : 'your catalog analysis'
  const {
    searchParams,
    setSearchParams,
    searchText,
    setSearchText,
    page,
    setPage,
    getFilter,
    setFilter,
  } = useCommonParams()
  const [searchInput, setSearchInput] = useState(searchText)
  const [count, setCount] = useState(0)
  const [importing, setImporting] = useState<RmnDiscoveredProduct | null>(null)
  const [pendingIgnore, setPendingIgnore] = useState<RmnDiscoveredProduct | null>(null)
  const [ignoring, setIgnoring] = useState(false)
  const [autoMatching, setAutoMatching] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState<FlatCategoryOption[]>([])
  const [brandOptions, setBrandOptions] = useState<RmnNetworkBrand[]>([])
  const [discoveredBrandOptions, setDiscoveredBrandOptions] = useState<
    RmnDiscoveredBrandOption[]
  >([])

  const status = getFilter('status', 'pending')
  const categoryFilter = getFilter('category')
  const brandFilter = getFilter('brand')
  const discoveredBrandFilter = getFilter('discovered_brand')

  // Filter option sources: the crawled category tree, the CATALOG brands
  // (rows carry matched_brand_id once the promote or the post-crawl
  // brandMatchPass identifies them) and the still-DISCOVERED brands (linked
  // to rows by brand text only). The two brand filters combine (AND).
  useEffect(() => {
    let stale = false
    axiosInstant
      .get(rmnCategoriesEndpoint)
      .then((res) => {
        if (!stale) setCategoryOptions(flattenRmn((res.data || []) as RmnCategoryNode[]))
      })
      .catch(() => {
        if (!stale) setCategoryOptions([])
      })
    axiosInstant
      .get(rmnBrandsEndpoint)
      .then((res) => {
        if (!stale) setBrandOptions((res.data || []) as RmnNetworkBrand[])
      })
      .catch(() => {
        if (!stale) setBrandOptions([])
      })
    axiosInstant
      .get(rmnDiscoveredBrandsEndpoint)
      .then((res) => {
        if (!stale) setDiscoveredBrandOptions((res.data || []) as RmnDiscoveredBrandOption[])
      })
      .catch(() => {
        if (!stale) setDiscoveredBrandOptions([])
      })
    return () => {
      stale = true
    }
  }, [])

  useEffect(() => {
    if (searchInput === searchText) return undefined
    const t = setTimeout(() => setSearchText(searchInput), 350)
    return () => clearTimeout(t)
  }, [searchInput]) // searchText intentionally omitted — sync effect below owns it

  useEffect(() => {
    if (searchText !== searchInput) setSearchInput(searchText)
  }, [searchText]) // searchInput intentionally omitted — debounce effect above owns it

  const list = useResourceList<RmnDiscoveredProduct>({
    endpoint: rmnDiscoveredProductsEndpoint,
    pageSize: PAGE_SIZE,
    errorLabel: 'Failed to load discovered products',
    searchText,
    searchParams,
    setSearchParams,
    page,
    setPage,
    onCountChange: setCount,
    extraParams: {
      match_status: status,
      rmn_category_id: categoryFilter || undefined,
      brand_id: brandFilter || undefined,
      rmn_discovered_brand_id: discoveredBrandFilter || undefined,
    },
  })

  const productLabel = (p: RmnDiscoveredProduct) => p.title || p.url

  const handleAutoMatch = async () => {
    setAutoMatching(true)
    try {
      const { data } = await axiosInstant.post(rmnDiscoveredProductsAutoMatchEndpoint)
      const remaining = data && data.remaining ? ` (${data.remaining} left to scan)` : ''
      toast.success(`Matched ${data.matched} of ${data.scanned} scanned${remaining}`)
      await list.initLoad(1)
    } catch (err: any) {
      if (err && err.response && err.response.status === 429) {
        toast.error('Auto-match was run recently — try again in a few minutes.')
      } else {
        toast.error("Couldn't run auto-match. Please try again.")
      }
    } finally {
      setAutoMatching(false)
    }
  }

  const handleIgnore = async () => {
    if (!pendingIgnore) return
    const label = productLabel(pendingIgnore)
    setIgnoring(true)
    try {
      await axiosInstant.post(rmnIgnoreDiscoveredProductEndpoint(pendingIgnore.id))
      toast.success(`Ignored "${label}"`)
      setPendingIgnore(null)
      await list.initLoad(1)
    } catch {
      toast.error(`Couldn't ignore "${label}". Please try again.`)
    } finally {
      setIgnoring(false)
    }
  }

  const columns: DataTableColumn<RmnDiscoveredProduct>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (p) => (
        <div className="flex items-center gap-[10px] min-w-0">
          <div className="w-[40px] h-[40px] rounded-[7px] bg-surface-2 border border-border-token shrink-0 overflow-hidden flex items-center justify-center">
            {p.image ? (
              <img src={p.image} alt="" className="w-full h-full object-contain" />
            ) : (
              <span className="text-[10px] text-muted">—</span>
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <a
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-text-1 hover:underline truncate max-w-[360px]"
              title={p.url}
            >
              {productLabel(p)}
            </a>
            {p.matched_desire && (
              <span
                className="text-[12px] text-up truncate"
                title="Importing links to this existing product — it won't create a duplicate"
              >
                ↳ Links to “{p.matched_desire.title}”
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'pdp_url',
      header: 'PDP URL',
      // The crawled product page itself — the URL the import attaches as
      // the product's PDP. Same treatment as the Brands › Discovered URL.
      render: (p) =>
        p.url ? (
          <a
            href={p.url}
            target="_blank"
            rel="noreferrer"
            title={p.url}
            className="text-text-2 hover:underline truncate inline-block max-w-[260px] align-middle"
          >
            {p.url.replace(/^https?:\/\/(www\.)?/, '')}
          </a>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'brand',
      header: 'Brand',
      render: (p) =>
        p.matched_brand ? (
          <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-accent-text bg-tint rounded-full px-2.5 py-1 whitespace-nowrap">
            {p.matched_brand.name}
          </span>
        ) : (
          <span className="text-text-2">{p.brand_text || '—'}</span>
        ),
    },
    {
      key: 'category',
      // Suggested, not assigned: the crawl's category passes propose this and
      // the import modal's required picker confirms it.
      header: 'Suggested TDC Category',
      render: (p) =>
        // "Uncategorized" is the promote's fallback, not a real suggestion.
        p.product_category && p.product_category.name !== 'Uncategorized' ? (
          <Tooltip title={p.product_category.name}>
            <span className="inline-block bg-surface-2 text-text-2 text-[11px] font-semibold rounded-full px-2.5 py-1 max-w-[180px] truncate align-middle">
              {p.product_category.name}
            </span>
          </Tooltip>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'rmn_category',
      // Where the retailer files it on their own site, e.g. "Sam's Club
      // Category" — same column the Catalog tab shows for imported products.
      header: `${brandName || 'Network'} Category`,
      render: (p) =>
        p.rmn_category ? (
          <Tooltip title={p.rmn_category.name}>
            <span className="inline-block bg-tint text-accent-text text-[11px] font-semibold rounded-full px-2.5 py-1 max-w-[180px] truncate align-middle">
              {p.rmn_category.name}
            </span>
          </Tooltip>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => <StatusPill status={p.match_status} />,
    },
    {
      key: 'actions',
      header: '',
      width: 260,
      render: (p) =>
        isActionable(p.match_status) ? (
          <div className="flex items-center gap-1 justify-end">
            <button
              type="button"
              title={
                p.matched_desire
                  ? `Links this page to "${p.matched_desire.title}" — won't create a duplicate`
                  : 'Import this product into your catalog'
              }
              onClick={() => setImporting(p)}
              className="text-[12px] font-semibold text-white bg-accent rounded-md px-2.5 py-1.5 border-0 cursor-pointer hover:bg-[#8A691F]"
            >
              {p.matched_desire ? 'Link' : 'Import'}
            </button>
            <button
              type="button"
              title="Ignore — won't appear in future reviews"
              onClick={() => setPendingIgnore(p)}
              className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2"
            >
              Ignore
            </button>
          </div>
        ) : null,
    },
  ]

  const showCrawlEmptyState =
    status === 'pending' &&
    !searchText &&
    !categoryFilter &&
    !brandFilter &&
    !discoveredBrandFilter &&
    !list.loading &&
    !list.error &&
    count === 0

  const filterSelectClass =
    'text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2 py-1.5 bg-surface cursor-pointer max-w-[190px]'

  const statusChips = (
    <div className="flex items-center gap-1.5 flex-wrap">
      <select
        aria-label="Filter by category"
        value={categoryFilter}
        onChange={(e) => setFilter('category', e.target.value || null)}
        className={filterSelectClass}
      >
        <option value="">{domain ? `All ${domain} categories` : 'All categories'}</option>
        {categoryOptions.map((o) => (
          <option key={o.id} value={o.id}>
            {'\u00A0'.repeat(o.depth * 3)}
            {o.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Filter by brand"
        value={brandFilter}
        onChange={(e) => setFilter('brand', e.target.value || null)}
        className={filterSelectClass}
      >
        <option value="">All catalog brands</option>
        <option value="none">Unmatched</option>
        {brandOptions
          .filter((b) => (b.discovered_product_count || 0) > 0)
          .map((b) => (
            <option key={b.id} value={b.id}>
              {b.name} ({b.discovered_product_count})
            </option>
          ))}
      </select>
      <select
        aria-label="Filter by discovered brand"
        value={discoveredBrandFilter}
        onChange={(e) => setFilter('discovered_brand', e.target.value || null)}
        className={filterSelectClass}
      >
        <option value="">All discovered brands</option>
        {discoveredBrandOptions.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name} ({b.product_count})
          </option>
        ))}
      </select>
      {STATUS_FILTERS.map((f) => {
        const active = status === f.value
        return (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter('status', f.value === 'pending' ? null : f.value)}
            className={
              active
                ? 'text-[12px] font-semibold text-white bg-accent rounded-full px-3 py-1.5 border-0 cursor-pointer'
                : 'text-[12px] font-semibold text-text-2 border border-border-token rounded-full px-3 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2'
            }
          >
            {f.label}
          </button>
        )
      })}
      <button
        type="button"
        disabled={autoMatching}
        onClick={handleAutoMatch}
        title="Match pending products against your existing catalog"
        className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed ml-1"
      >
        {autoMatching ? 'Matching…' : 'Auto-match'}
      </button>
    </div>
  )

  return (
    <MainContainer>
      <button
        type="button"
        onClick={() => navigate(`${basePath}/products`)}
        className="text-[13px] font-semibold text-text-2 bg-transparent border-0 cursor-pointer hover:text-text-1 mb-3 px-0"
      >
        ← Back to Catalog
      </button>
      <ListPageHeader
        title="Discovered Products"
        subtitle={
          count > 0
            ? `Products discovered by ${analysisLabel} · ${count} total`
            : `Products discovered by ${analysisLabel}`
        }
        tabs={<CatalogTabs tabs={PRODUCT_CATALOG_TABS} />}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchInputId="search-discovered-products"
        searchPlaceholder="Search by title…"
        searchRightContent={statusChips}
      />

      {showCrawlEmptyState ? (
        <div className="max-w-[560px] text-[14px] text-text-2 leading-relaxed py-10">
          <p className="font-semibold text-text-1 mb-2">No products to review</p>
          <p>
            Products appear here once your catalog has been analyzed. Contact your
            Desire Company account manager to set up an analysis for your brand.
          </p>
        </div>
      ) : (
        <DataTable<RmnDiscoveredProduct>
          columns={columns}
          rows={list.items}
          loading={list.loading}
          totalCount={list.totalCount}
          hasMore={list.hasMore}
          error={list.error}
          onLoadMore={list.loadMore}
          emptyMessage="No discovered products match this view"
          getRowKey={(r) => r.id}
        />
      )}

      {importing && (
        <ImportDiscoveredModal
          mode="product"
          record={importing}
          onClose={() => setImporting(null)}
          onImported={async () => {
            setImporting(null)
            await list.initLoad(1)
          }}
        />
      )}

      {pendingIgnore && (
        <ConfirmationDialog
          open
          title="Ignore Product"
          description={`Ignore "${productLabel(pendingIgnore)}"? Ignored products won't appear in future reviews and won't be imported.`}
          confirmLabel="Ignore"
          onClose={() => setPendingIgnore(null)}
          onConfirm={handleIgnore}
          disabled={ignoring}
        />
      )}
    </MainContainer>
  )
}
